import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * HTTP Interceptor que agrega el token JWT a todas las requests autenticadas.
 * Excluye endpoints públicos como login y entra callback.
 * Implementa auto-refresh del token cuando recibe un 401.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Endpoints públicos que no requieren autenticación
  const publicEndpoints = [
    '/api/auth/login',
    '/api/auth/entra/login-url',
    '/api/auth/entra/callback',
    '/api/auth/refresh'
  ];

  // Verificar si es un endpoint público
  const isPublicEndpoint = publicEndpoints.some((endpoint) =>
    req.url.includes(endpoint)
  );

  if (isPublicEndpoint) {
    return next(req);
  }

  // Obtener el token y agregarlo al header
  const token = authService.getToken();
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.includes('/api/auth/')) {
        return handleTokenRefresh(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};

function handleTokenRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response) => {
        isRefreshing = false;
        refreshTokenSubject.next(response.access_token);

        const retryReq = req.clone({
          setHeaders: { Authorization: `Bearer ${response.access_token}` }
        });
        return next(retryReq);
      }),
      catchError((err) => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        return throwError(() => err);
      })
    );
  }

  // Si ya se está refrescando, esperar al nuevo token
  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => {
      const retryReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` }
      });
      return next(retryReq);
    })
  );
}
